import React, { useEffect, useMemo, useState } from 'react';
import { Flex, Grid } from '@mantine/core';

import useDesktop from '@/hooks/useDesktop';
import { HEADER_HEIGHT } from '@/constants/variables';

import type { RestaurantKey, VendorAvailabilityListItem } from './types';
import { useAvailabilityList } from './hooks/useAvailabilityList';
import AvailabilityPageHeader from './Availability.page.header';
import RestaurantListSection from './RestaurantList.section/RestaurantList.section';
import AvailabilityCalendarSection from './AvailabilityCalendar.section/AvailabilityCalendar.section';
import AvailabilityCalendarEmpty from './AvailabilityCalendar.section/AvailabilityCalendar.empty';

const AvailabilityPage: React.FC = () => {
  const isDesktop = useDesktop();

  const { loading, restaurants, refetch } = useAvailabilityList();

  const [selectedKey, setSelectedKey] = useState<RestaurantKey | null>(null);

  const selectedRestaurant = useMemo<VendorAvailabilityListItem | undefined>(
    () =>
      selectedKey
        ? restaurants.find(
            (r) => r.vendorId === selectedKey.vendorId && r.productType === selectedKey.productType
          )
        : undefined,
    [selectedKey, restaurants]
  );

  useEffect(() => {
    if (restaurants.length === 0 || selectedKey) return;

    setSelectedKey({
      vendorId: restaurants[0].vendorId,
      productType: restaurants[0].productType,
    });
  }, [restaurants, selectedKey]);

  return (
    <Flex direction="column" h={isDesktop ? `calc(100vh - ${HEADER_HEIGHT}px)` : undefined}>
      <AvailabilityPageHeader />

      <Grid
        gutter={0}
        flex={isDesktop ? 1 : undefined}
        mih={isDesktop ? 0 : undefined}
        styles={isDesktop ? { inner: { height: '100%' } } : undefined}
      >
        <Grid.Col
          span={isDesktop ? 3 : 12}
          h={isDesktop ? '100%' : undefined}
          style={
            isDesktop
              ? { borderRight: '1px solid var(--mantine-color-gray-3)', overflow: 'hidden' }
              : undefined
          }
        >
          <RestaurantListSection
            restaurants={restaurants}
            loading={loading}
            selectedKey={selectedKey}
            onSelect={setSelectedKey}
          />
        </Grid.Col>
        <Grid.Col
          span={isDesktop ? 9 : 12}
          h={isDesktop ? '100%' : undefined}
          style={isDesktop ? { overflow: 'auto' } : undefined}
        >
          {selectedKey && selectedRestaurant ? (
            <AvailabilityCalendarSection
              vendorId={selectedKey.vendorId}
              productType={selectedKey.productType}
              restaurant={selectedRestaurant}
              refetchAvailabilityList={refetch}
            />
          ) : (
            <AvailabilityCalendarEmpty />
          )}
        </Grid.Col>
      </Grid>
    </Flex>
  );
};

export default AvailabilityPage;
